import { useParams, Link } from 'react-router-dom'
import { useEffect, useMemo, useState, ElementType } from 'react'
import { Helmet } from 'react-helmet-async'
import { API_BASE } from '../config/api'
import {
  FaGithub, FaLinkedin, FaTwitter, FaInstagram, FaDribbble,
  FaBehance, FaPinterest, FaYoutube, FaMedium, FaLink
} from 'react-icons/fa'
import { TbWorldWww } from 'react-icons/tb'

// Helper function to check if string is an image URL
const isImageUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') return false
  const str = value.trim()
  if (!str) return false
  return str.startsWith('http://') || str.startsWith('https://') || str.startsWith('/') || str.includes('.')
}

type SocialLinks = Record<string, string>

interface TeamMember {
  id: string
  name: string
  role: string
  avatar: string
  experience: string
  quote: string
  email: string
  bio: string
  isCoreTeamMember?: boolean
  github?: string
  linkedin?: string
  expertise: string[]
  specializations: string[]
  socialLinks: SocialLinks
  projects: Array<{ name: string; description: string; technologies: string[]; status: string }>
  personalInterests: Array<{ name: string; description: string; icon: string }>
  currentLearning: Array<{ topic: string; progress: string; goal: string; timeline: string }>
  achievements: string[]
  workStyle?: { preferredEnvironment?: string; bestTimeToWork?: string; communicationStyle?: string }
  funFacts: string[]
}

interface PersonalProjectSummary {
  id: string
  title: string
  description: string
  image: string
  category?: string
  status?: string
  technologies: string[]
  owner?: string
}

export default function TeamDetail() {
  const { id } = useParams<{ id: string }>()
  const [member, setMember] = useState<TeamMember | null>(null)
  const [personalProjects, setPersonalProjects] = useState<PersonalProjectSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const encodedId = useMemo(() => (id ? encodeURIComponent(id) : ''), [id])

  useEffect(() => {
    let isMounted = true
    const fetchMember = async () => {
      if (!id) {
        setMember(null)
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError(null)
      try {
        const res = await fetch(`${API_BASE}/api/v1/team/${encodedId}`)
        if (!res.ok) {
          throw new Error(`Team API request failed: ${res.status} ${res.statusText}`)
        }
        const json = await res.json()
        const raw = (json?.data ?? json) as Record<string, unknown>

        const socialLinksRaw = (raw.socialLinks ?? {}) as Record<string, unknown>
        const socialLinks: SocialLinks = {}
        Object.entries(socialLinksRaw).forEach(([k, v]) => {
          if (typeof v === 'string' && v.trim()) socialLinks[k] = v
        })

        const isCoreTeamMember = typeof raw.isCoreTeamMember === 'boolean'
          ? raw.isCoreTeamMember
          : raw.isCoreTeamMember === undefined ? true : (raw.isCoreTeamMember === 'true' || raw.isCoreTeamMember === 1 || raw.isCoreTeamMember === '1')

        const normalized: TeamMember = {
          id: typeof raw.id === 'string' ? raw.id : id,
          name: typeof raw.name === 'string' ? raw.name : 'Unnamed',
          role: typeof raw.role === 'string' ? raw.role : '',
          avatar: typeof raw.avatar === 'string' && raw.avatar ? raw.avatar : '👤',
          experience: typeof raw.experience === 'string' ? raw.experience : '',
          quote: typeof raw.quote === 'string' ? raw.quote : '',
          email: typeof raw.email === 'string' ? raw.email : '',
          bio: typeof raw.bio === 'string' ? raw.bio : '',
          isCoreTeamMember,
          github: typeof raw.github === 'string' ? raw.github : undefined,
          linkedin: typeof raw.linkedin === 'string' ? raw.linkedin : undefined,
          expertise: Array.isArray(raw.expertise) ? raw.expertise.filter((s): s is string => typeof s === 'string') : [],
          specializations: Array.isArray(raw.specializations) ? raw.specializations.filter((s): s is string => typeof s === 'string') : [],
          socialLinks,
          projects: Array.isArray(raw.projects) ? (raw.projects as TeamMember['projects']) : [],
          personalInterests: Array.isArray(raw.personalInterests) ? (raw.personalInterests as TeamMember['personalInterests']) : [],
          currentLearning: Array.isArray(raw.currentLearning) ? (raw.currentLearning as TeamMember['currentLearning']) : [],
          achievements: Array.isArray(raw.achievements) ? raw.achievements.filter((s): s is string => typeof s === 'string') : [],
          workStyle: (typeof raw.workStyle === 'object' && raw.workStyle !== null) ? (raw.workStyle as TeamMember['workStyle']) : undefined,
          funFacts: Array.isArray(raw.funFacts) ? raw.funFacts.filter((s): s is string => typeof s === 'string') : [],
        }

        if (isMounted) {
          setMember(normalized)
        }
      } catch (e) {
        if (isMounted) {
          setMember(null)
          setError(e instanceof Error ? e.message : 'Failed to load team member')
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchMember()
    return () => {
      isMounted = false
    }
  }, [encodedId, id])

  useEffect(() => {
    let isMounted = true
    const fetchPersonalProjects = async () => {
      if (!member) {
        setPersonalProjects([])
        return
      }
      try {
        const res = await fetch(`${API_BASE}/api/v1/projects?type=personal`)
        if (!res.ok) {
          throw new Error('Failed to load projects')
        }
        const json = await res.json()
        const list = Array.isArray(json?.data) ? json.data : []
        const normalized = list
          .map((p: unknown, index: number) => {
            const obj = (p ?? {}) as Record<string, unknown>
            return {
              id: typeof obj.id === 'string' && obj.id ? obj.id : `proj-${index}`,
              title: typeof obj.title === 'string' ? obj.title : 'Untitled Project',
              description: typeof obj.description === 'string' ? obj.description : '',
              image: typeof obj.image === 'string' && obj.image ? obj.image : '📱',
              category: typeof obj.category === 'string' ? obj.category : undefined,
              status: typeof obj.status === 'string' ? obj.status : undefined,
              technologies: Array.isArray(obj.technologies) ? obj.technologies.filter((t): t is string => typeof t === 'string') : [],
              owner: typeof obj.owner === 'string' ? obj.owner : undefined,
            } satisfies PersonalProjectSummary
          })

        if (isMounted) {
          setPersonalProjects(normalized)
        }
      } catch {
        if (isMounted) {
          setPersonalProjects([])
        }
      }
    }

    fetchPersonalProjects()
    return () => {
      isMounted = false
    }
  }, [member])

  const personalProjectByTitle = useMemo(() => {
    const map = new Map<string, PersonalProjectSummary>()
    personalProjects.forEach((p) => {
      map.set((p.title || '').trim().toLowerCase(), p)
    })
    return map
  }, [personalProjects])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="text-center p-8">
          <p className="text-sm font-mono uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Loading profile...</p>
        </div>
      </div>
    )
  }

  if (!member) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="text-center max-w-md mx-auto p-8 rounded-card border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <h1 className="font-display text-2xl font-bold mb-4" style={{ color: 'var(--text)' }}>
            Team Member Not Found
          </h1>
          <p className="text-sm mb-8 leading-relaxed" style={{ color: 'var(--muted)' }}>
            {error ? error : "Sorry, we couldn't find the team member you're looking for."}
          </p>
          <Link to="/team" className="btn-pill btn-primary no-underline inline-block">
            ← Back to Team
          </Link>
        </div>
      </div>
    )
  }

  const stats = [
    { label: 'Experience', value: member.experience },
    ...(member.projects.length > 0 ? [{ label: 'Projects', value: `${member.projects.length}+` }] : []),
    { label: 'Specializations', value: member.specializations.length.toString() },
    { label: 'Status', value: 'Available' },
  ]

  return (
    <div className="min-h-screen pt-32 pb-16" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <Helmet>
        <title>{member.name} | Ionic Errrrs Code</title>
        <meta name="description" content={member.bio.substring(0, 155)} />
        <link rel="canonical" href={`https://ionicerrrrscode.com/team/${member.id}`} />
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content="Ionic Errrrs Code" />
        <meta property="og:title" content={`${member.name} - ${member.role} | Ionic Errrrs Code`} />
        <meta property="og:description" content={member.bio.substring(0, 155)} />
        <meta property="og:url" content={`https://ionicerrrrscode.com/team/${member.id}`} />
        {member.avatar && <meta property="og:image" content={member.avatar} />}
      </Helmet>

      <div className="container-page max-w-4xl px-4">
        {/* Back Button */}
        <div className="mb-10">
          <Link 
            to="/team" 
            className="inline-flex items-center text-sm font-semibold no-underline hover:underline"
            style={{ color: 'var(--text)' }}
          >
            ← Back to Team
          </Link>
        </div>

        {/* Member Header */}
        <div className="text-center mb-16">
          <div className="relative inline-block mb-6">
            <div 
              className="w-40 h-40 rounded-full flex items-center justify-center text-5xl border overflow-hidden shadow-sm"
              style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
            >
              {isImageUrl(member.avatar) ? (
                <img
                  src={member.avatar}
                  alt={member.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    (e.target as HTMLImageElement).nextElementSibling!.classList.remove('hidden');
                  }}
                />
              ) : (
                member.avatar
              )}
              <div className="hidden w-full h-full flex items-center justify-center text-5xl">
                👤
              </div>
            </div>
          </div>

          <h1 className="font-display text-4xl font-bold mb-4" style={{ letterSpacing: '-0.02em' }}>
            {member.name}
          </h1>

          <div className="flex flex-wrap justify-center gap-2 mb-6">
            <span className="badge" style={{ background: 'var(--btn-bg)', color: 'var(--btn-text)', border: '1px solid var(--btn-bg)' }}>
              {member.role}
            </span>
            <span className="badge">
              {member.experience}
            </span>
          </div>

          {member.quote && (
            <p className="text-[1.12rem] leading-relaxed max-w-2xl mx-auto italic" style={{ color: 'var(--muted)' }}>
              &ldquo;{member.quote}&rdquo;
            </p>
          )}

          {/* Social Links */}
          {member.isCoreTeamMember !== false && member.socialLinks && Object.keys(member.socialLinks).length > 0 && (
            <div className="flex justify-center gap-3 mt-6">
              {Object.entries(member.socialLinks).map(([platform, url]) => {
                const getSocialIcon = (platform: string) => {
                  const icons: Record<string, ElementType> = {
                    github: FaGithub,
                    linkedin: FaLinkedin,
                    twitter: FaTwitter,
                    instagram: FaInstagram,
                    dribbble: FaDribbble,
                    behance: FaBehance,
                    pinterest: FaPinterest,
                    website: TbWorldWww,
                    medium: FaMedium,
                    youtube: FaYoutube
                  }
                  return icons[platform] || FaLink
                }

                const Icon = getSocialIcon(platform)

                return (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full flex items-center justify-center border transition-colors shadow-sm"
                    style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--muted)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.borderColor = 'var(--border-strong)' }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--muted)'; e.currentTarget.style.borderColor = 'var(--border)' }}
                    title={platform.charAt(0).toUpperCase() + platform.slice(1)}
                  >
                    <Icon size={18} />
                  </a>
                )
              })}
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div
          className="stats-bar rounded-card overflow-hidden mb-12"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${stats.length}, 1fr)`,
            border: '1px solid var(--border)',
            background: 'var(--surface)',
          }}
        >
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className="text-center py-6 px-4"
              style={{
                borderRight: i < stats.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              <div className="stat-number">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Skills & Technologies */}
        {member.expertise.length > 0 && (
          <div className="mb-12 rounded-card border p-8" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <h2 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)', letterSpacing: '-0.01em' }}>
              Core Skills & Technologies
            </h2>
            <div className="flex flex-wrap gap-2">
              {member.expertise.map((skill) => (
                <span key={skill} className="tag-chip">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* About Bio Section */}
        {member.bio && (
          <div className="mb-12 rounded-card border p-8" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <h2 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)', letterSpacing: '-0.01em' }}>
              About {member.name.split(' ')[0]}
            </h2>
            <p className="text-base leading-relaxed mb-0" style={{ color: 'var(--muted)' }}>
              {member.bio}
            </p>
          </div>
        )}

        {/* Expertise Areas */}
        {member.specializations.length > 0 && (
          <div className="mb-12">
            <h2 className="font-display text-xl font-bold mb-6 text-center" style={{ color: 'var(--text)' }}>
              Areas of Expertise
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {member.specializations.map((area) => (
                <div
                  key={area}
                  className="card-studio p-6"
                >
                  <h3 className="font-display text-base font-bold mb-2" style={{ color: 'var(--text)' }}>
                    {area}
                  </h3>
                  <p className="text-sm leading-relaxed mb-0" style={{ color: 'var(--muted)' }}>
                    Expert level proficiency with years of hands-on experience in production environments.
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Personal Projects Section */}
        {member.projects.length > 0 && (
          <div className="mb-12">
            <h2 className="font-display text-xl font-bold mb-6 text-center" style={{ color: 'var(--text)' }}>
              Personal Projects & Side Work
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {member.projects.map((project, index) => {
                const matched = personalProjectByTitle.get((project.name || '').trim().toLowerCase())
                const title = matched?.title || project.name
                const description = matched?.description || project.description
                const status = matched?.status || project.status
                const technologies = (matched?.technologies?.length ? matched.technologies : project.technologies) || []

                const content = (
                  <div className="card-studio h-full p-6 flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-lg overflow-hidden flex flex-shrink-0 items-center justify-center border text-2xl"
                            style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
                          >
                            {matched && isImageUrl(matched.image) ? (
                              <img
                                src={matched.image}
                                alt={title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement
                                  target.style.display = 'none'
                                  target.nextElementSibling?.classList.remove('hidden')
                                }}
                              />
                            ) : null}
                            <span className={`${matched && isImageUrl(matched.image) ? 'hidden' : ''}`}>
                              {matched ? matched.image : '🚀'}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-display text-base font-bold mb-0.5" style={{ color: 'var(--text)' }}>
                              {title}
                            </h4>
                            {matched?.category && (
                              <span className="text-xs font-mono uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                                {matched.category}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="badge">
                          <span className="badge-dot" style={{ background: status === 'Completed' || status === 'Live' ? 'var(--accent)' : '#eab308' }} />
                          {status}
                        </span>
                      </div>

                      <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--muted)' }}>
                        {description}
                      </p>
                    </div>

                    <div>
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {technologies.slice(0, 3).map((tech) => (
                          <span key={tech} className="tag-chip text-xs">
                            {tech}
                          </span>
                        ))}
                        {technologies.length > 3 && (
                          <span className="tag-chip text-xs opacity-75">
                            +{technologies.length - 3}
                          </span>
                        )}
                      </div>
                      {matched && (
                        <div className="text-sm font-semibold hover:underline mt-2" style={{ color: 'var(--text)' }}>
                          View Details →
                        </div>
                      )}
                    </div>
                  </div>
                )

                return (
                  <div key={`${project.name}-${index}`}>
                    {matched ? (
                      <Link to={`/personal-projects/${matched.id}`} className="no-underline block h-full">
                        {content}
                      </Link>
                    ) : (
                      content
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Currently Learning Section */}
        {member.currentLearning.length > 0 && (
          <div className="mb-12">
            <h2 className="font-display text-xl font-bold mb-6 text-center" style={{ color: 'var(--text)' }}>
              Currently Learning & Growing
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {member.currentLearning.map((learning) => (
                <div key={learning.topic} className="card-studio p-6 flex flex-col justify-between">
                  <div>
                    <div className="text-2xl mb-3">📚</div>
                    <h3 className="font-display text-base font-bold mb-3" style={{ color: 'var(--text)' }}>
                      {learning.topic}
                    </h3>
                    <div className="mb-4">
                      <div className="flex justify-between text-xs font-mono mb-2" style={{ color: 'var(--muted)' }}>
                        <span>Progress</span>
                        <span>{learning.progress}</span>
                      </div>
                      <div className="w-full rounded-full h-1.5" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                        <div 
                          className="h-full rounded-full" 
                          style={{ width: learning.progress, background: 'var(--accent)' }}
                        />
                      </div>
                    </div>
                    <p className="text-xs mb-2 leading-relaxed" style={{ color: 'var(--muted)' }}>
                      <strong>Goal:</strong> {learning.goal}
                    </p>
                  </div>
                  <div className="text-xs font-mono border-t pt-3 mt-3" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>
                    Timeline: {learning.timeline}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Personal Interests Section */}
        {member.personalInterests.length > 0 && (
          <div className="mb-12">
            <h2 className="font-display text-xl font-bold mb-6 text-center" style={{ color: 'var(--text)' }}>
              Interests & Hobbies
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {member.personalInterests.map((interest) => (
                <div key={interest.name} className="card-studio text-center p-6">
                  <div className="text-3xl mb-3">
                    {interest.icon}
                  </div>
                  <h3 className="font-display text-base font-bold mb-2" style={{ color: 'var(--text)' }}>
                    {interest.name}
                  </h3>
                  <p className="text-xs leading-relaxed mb-0" style={{ color: 'var(--muted)' }}>
                    {interest.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Fun Facts Section */}
        {member.funFacts.length > 0 && (
          <div className="mb-12 rounded-card border p-8" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <h2 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)', letterSpacing: '-0.01em' }}>
              Fun Facts About {member.name.split(' ')[0]}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {member.funFacts.map((fact, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3"
                >
                  <span style={{ color: 'var(--border-strong)' }}>◦</span>
                  <p className="text-sm leading-relaxed mb-0" style={{ color: 'var(--muted)' }}>
                    {fact}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Closing Contact CTA */}
        <div className="mt-16 text-center">
          <div className="cta-block" style={{ maxWidth: '700px', margin: '0 auto' }}>
            <h3 className="font-display text-2xl font-semibold" style={{ color: 'var(--inverse-text)' }}>
              Ready to Work Together?
            </h3>
            <p className="mt-3 text-[1.06rem]" style={{ color: 'var(--inverse-text)', opacity: 0.6, maxWidth: '500px', margin: '12px auto 0' }}>
              Let&apos;s discuss your project and see how {member.name.split(' ')[0]} can help bring your ideas to life.
            </p>
            <div className="mt-8 flex justify-center gap-4">
              <Link to="/contact" className="btn-pill btn-inverse no-underline">
                Get In Touch
              </Link>
              <Link to="/team" className="btn-pill btn-ghost no-underline" style={{ borderColor: 'var(--inverse-text)', color: 'var(--inverse-text)' }}>
                All Team Members
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
